import { apiWorkflows } from "@/api/workflows/workflow.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiWorkflowBlockActionCandidateEmailSave,
  ApiWorkflowBlockActionCandidateStageChangeSave,
  ApiWorkflowBlockActionGoogleWorkspaceSave,
  ApiWorkflowBlockActionHTTPRequestSave,
  ApiWorkflowBlockActionInvitationSave,
  ApiWorkflowBlockActionNotificationSave,
  ApiWorkflowBlockActionApprovalSave,
  ApiWorkflowBlockActionEsignSave,
  ApiWorkflowBlockActionPerformanceReviewEnrollSave,
  ApiWorkflowBlockActionPerformanceReviewSave,
  ApiWorkflowBlockActionRunWorkflowSave,
  ApiWorkflowBlockActionSavePayload,
  ApiWorkflowBlockActionSurveyEnrollSave,
  ApiWorkflowBlockActionSurveySave,
  ApiWorkflowBlockActionTaskSave,
  ApiWorkflowBlockActionMeetingSave,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowConditionSave } from "@/types/workflows/workflow.condition.types.ts";
import { workflowFoldersKeys } from "@/queries/workflows/workflow-folder.query.ts";
import {
  ApiWorkflowBlockMove,
  ApiWorkflowBlockEdge,
  ApiWorkflowBlockSave,
  ApiWorkflowCreate,
  ApiWorkflowFilter,
  ApiWorkflowTriggerScheduleRuleSave,
  ApiWorkflowTriggerRecurringRule,
  ApiWorkflowUpdateDetails,
  ApiWorkflowUpdateTrigger,
  ApiWorkflowVersion,
} from "@/types/workflows/workflow.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import { useDebounce } from "react-use";

const workflowKeys = createQueryKeys("workflows", {
  search: (filter: ApiWorkflowFilter) => ({ queryKey: [filter] }),
  shared: () => ({ queryKey: ["shared"] }),
  byId: (workflowId: number | undefined) => ({
    queryKey: [workflowId],
    queryFn: async () => {
      if (workflowId == undefined) throw new ApiPassException("Id is required");
      const result = await apiWorkflows.getById(workflowId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  summary: (workflowId: number | undefined) => ({ queryKey: [workflowId] }),
  meetingOrganizerCalendars: (workflowId: number | undefined, organizerId: number | undefined) => ({
    queryKey: [workflowId, organizerId],
    queryFn: async () => {
      if (workflowId == undefined || organizerId == undefined) {
        throw new ApiPassException("Id is required");
      }
      const result = await apiWorkflows.getMeetingOrganizerCalendars(workflowId, organizerId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  versionById: (workflowId: number | undefined, versionId: number | undefined) => ({
    queryKey: [workflowId, versionId],
    queryFn: async () => {
      if (workflowId == undefined) throw new ApiPassException("Id is required");
      if (versionId == undefined) throw new ApiPassException("Id is required");
      const result = await apiWorkflows.getVersionById(workflowId, versionId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

interface WorkflowBlockMoveVariables {
  workflowId: number;
  workflowVersionId: number;
  workflowBlockId: number;
  payload: ApiWorkflowBlockMove;
}

export const workflowBlockMoveMutationKey = ["workflowBlockMove"] as const;

const moveWorkflowBlockOptimistically = (
  version: ApiWorkflowVersion,
  variables: WorkflowBlockMoveVariables
): ApiWorkflowVersion => {
  const movedEdge = version.edges.find((edge) => edge.to_block_id === variables.workflowBlockId);
  if (!movedEdge) return version;

  const oldParentId = movedEdge.from_block_id;
  const newParentId = variables.payload.parent_block_id;
  const sortEdges = (left: ApiWorkflowBlockEdge, right: ApiWorkflowBlockEdge) =>
    left.priority - right.priority || left.id - right.id;
  const oldSiblings = version.edges
    .filter((edge) => edge.from_block_id === oldParentId && edge.id !== movedEdge.id)
    .sort(sortEdges);
  const newSiblings =
    oldParentId === newParentId
      ? oldSiblings
      : version.edges
          .filter((edge) => edge.from_block_id === newParentId && edge.id !== movedEdge.id)
          .sort(sortEdges);

  if (variables.payload.position < 0 || variables.payload.position > newSiblings.length) {
    return version;
  }

  const targetSiblings = [...newSiblings];
  targetSiblings.splice(variables.payload.position, 0, movedEdge);

  const edgeUpdates = new Map<number, { fromBlockId: number; priority: number }>();
  targetSiblings.forEach((edge, priority) => {
    edgeUpdates.set(edge.id, { fromBlockId: newParentId, priority });
  });

  if (oldParentId !== newParentId) {
    oldSiblings.forEach((edge, priority) => {
      edgeUpdates.set(edge.id, { fromBlockId: oldParentId, priority });
    });
  }

  return {
    ...version,
    edges: version.edges.map((edge) => {
      const update = edgeUpdates.get(edge.id);
      return update
        ? { ...edge, from_block_id: update.fromBlockId, priority: update.priority }
        : edge;
    }),
  };
};

export const useWorkflowsSearch = ({
  filter,
  enabled = true,
}: {
  filter: ApiWorkflowFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...workflowKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiWorkflows.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkflowsSearchInline = (enabled: boolean = true) => {
  const [text, setText] = useState<string | undefined>(undefined);
  const [debouncedText, setDebouncedText] = useState<string | undefined>(undefined);

  useDebounce(
    () => {
      setDebouncedText(text);
    },
    300,
    [text]
  );

  const filter = useMemo<ApiWorkflowFilter>(
    () => ({ page_number: 1, text: debouncedText || undefined }),
    [debouncedText]
  );

  const { data, isFetching } = useWorkflowsSearch({ filter, enabled });

  const items = data?.items ?? [];

  function search(value: string | undefined) {
    setText(value);
  }

  return { items, isFetching, search };
};

export const useWorkflowsShared = (enabled: boolean = true) => {
  return useQuery({
    ...workflowKeys.shared(),
    enabled: enabled,
    queryFn: async () => {
      const result = await apiWorkflows.getShared();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkflow = (workflowId: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...workflowKeys.byId(workflowId),
    enabled: enabled,
  });
};

export const useWorkflowsByIds = (workflowIds: number[], enabled: boolean = true) => {
  return useQueries({
    queries: workflowIds.map((workflowId) => ({
      ...workflowKeys.byId(workflowId),
      enabled,
    })),
  });
};

export const useWorkflowVersion = (
  params: {
    workflowId: number;
    workflowVersionId: number;
  } | null,
  enabled: boolean = true
) => {
  return useQuery({
    ...workflowKeys.versionById(params?.workflowId, params?.workflowVersionId),
    enabled: enabled,
  });
};

export const useWorkflowSummary = (workflowId: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...workflowKeys.summary(workflowId),
    enabled: enabled && workflowId != undefined,
    queryFn: async ({ signal }) => {
      if (workflowId == undefined) throw new ApiPassException("Id is required");
      const result = await apiWorkflows.getSummary(workflowId, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useWorkflowCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ payload }: { payload: ApiWorkflowCreate }) {
      const result = await apiWorkflows.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowFoldersKeys.all._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ workflowId }: { workflowId: number }) {
      const result = await apiWorkflows.delete(workflowId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.summary(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowFoldersKeys.all._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useWorkflowDuplicate = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ workflowId }: { workflowId: number }) {
      const result = await apiWorkflows.duplicate(workflowId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowFoldersKeys.all._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useWorkflowUpdateDetails = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: ApiWorkflowUpdateDetails;
    }) {
      const result = await apiWorkflows.updateDetails(workflowId, workflowVersionId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowFoldersKeys.all._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateTrigger = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: ApiWorkflowUpdateTrigger;
    }) {
      const result = await apiWorkflows.updateTrigger(workflowId, workflowVersionId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.summary(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateTriggerScheduleRule = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: ApiWorkflowTriggerScheduleRuleSave;
    }) {
      const result = await apiWorkflows.updateTriggerScheduleRule(
        workflowId,
        workflowVersionId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateTriggerRecurringRule = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: ApiWorkflowTriggerRecurringRule;
    }) {
      const result = await apiWorkflows.updateTriggerRecurringRule(
        workflowId,
        workflowVersionId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.search._def });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.summary(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateTriggerConditions = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: ApiWorkflowConditionSave[];
    }) {
      const result = await apiWorkflows.updateTriggerConditions(
        workflowId,
        workflowVersionId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateIsActive = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: boolean;
    }) {
      const result = payload
        ? await apiWorkflows.activate(workflowId, workflowVersionId)
        : await apiWorkflows.deactivate(workflowId, workflowVersionId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.search._def,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.summary(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowCreateBlock = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      payload: ApiWorkflowBlockSave;
    }) {
      const result = await apiWorkflows.createBlock(workflowId, workflowVersionId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionNotification = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionNotificationSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionSendNotification(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionCandidateEmail = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionCandidateEmailSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionCandidateEmail(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionCandidateStageChange = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionCandidateStageChangeSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionCandidateStageChange(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionHTTPRequest = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionHTTPRequestSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionHTTPRequest(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowRotateBlockActionHTTPRequestSignatureSecret = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
    }) => {
      const result = await apiWorkflows.rotateBlockActionHTTPRequestSignatureSecret(
        workflowId,
        workflowVersionId,
        workflowBlockId
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => onError(error),
  });
};

export const useWorkflowUpdateBlockActionRunWorkflow = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionRunWorkflowSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionRunWorkflow(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionApproval = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionApprovalSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionApproval(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionGoogleWorkspace = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionGoogleWorkspaceSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionGoogleWorkspace(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionInvitation = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionInvitationSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionInvitation(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionSurvey = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionSurveySave>;
    }) {
      const result = await apiWorkflows.updateBlockActionSendSurvey(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionPerformanceReview = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionPerformanceReviewSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionPerformanceReview(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionPerformanceReviewEnroll = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionPerformanceReviewEnrollSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionPerformanceReviewEnroll(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionSurveyEnroll = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionSurveyEnrollSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionSurveyEnroll(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionTask = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionTaskSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionTask(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowMeetingOrganizerCalendars = (
  workflowId: number | undefined,
  organizerId: number | undefined
) => {
  return useQuery({
    ...workflowKeys.meetingOrganizerCalendars(workflowId, organizerId),
    enabled: workflowId != undefined && organizerId != undefined,
    staleTime: 60 * 1000,
  });
};

export const useWorkflowUpdateBlockActionMeeting = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionMeetingSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionMeeting(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowUpdateBlockActionEsign = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
      payload,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
      payload: ApiWorkflowBlockActionSavePayload<ApiWorkflowBlockActionEsignSave>;
    }) {
      const result = await apiWorkflows.updateBlockActionEsign(
        workflowId,
        workflowVersionId,
        workflowBlockId,
        payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowDeleteBlock = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
    }) {
      const result = await apiWorkflows.deleteBlock(workflowId, workflowVersionId, workflowBlockId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useWorkflowDuplicateBlock = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({
      workflowId,
      workflowVersionId,
      workflowBlockId,
    }: {
      workflowId: number;
      workflowVersionId: number;
      workflowBlockId: number;
    }) {
      const result = await apiWorkflows.duplicateBlock(
        workflowId,
        workflowVersionId,
        workflowBlockId
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.byId(variables.workflowId).queryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useWorkflowMoveBlock = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationKey: workflowBlockMoveMutationKey,
    mutationFn: async (variables: WorkflowBlockMoveVariables) => {
      const result = await apiWorkflows.moveBlock(
        variables.workflowId,
        variables.workflowVersionId,
        variables.workflowBlockId,
        variables.payload
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onMutate: async (variables) => {
      const queryKey = workflowKeys.versionById(
        variables.workflowId,
        variables.workflowVersionId
      ).queryKey;

      const cancelQueriesPromise = queryClient.cancelQueries({ queryKey });
      const previousVersion = queryClient.getQueryData<ApiWorkflowVersion>(queryKey);

      queryClient.setQueryData<ApiWorkflowVersion>(queryKey, (version) =>
        version ? moveWorkflowBlockOptimistically(version, variables) : version
      );

      await cancelQueriesPromise;

      return { previousVersion, queryKey };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousVersion) {
        queryClient.setQueryData(context.queryKey, context.previousVersion);
      }
      onError(error);
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: workflowKeys.versionById(variables.workflowId, variables.workflowVersionId)
          .queryKey,
        refetchType: "all",
      });
    },
  });
};
